import { CartUpdateEvent, ThemeEvents } from '@theme/events';

class ProductCardCartControl extends HTMLElement {
  static cartFetchPromise = null;

  connectedCallback() {
    this.variantId = this.dataset.variantId;
    this.available = this.dataset.available === 'true';
    this.min = Number(this.dataset.minQuantity) || 1;
    this.step = Number(this.dataset.step) || 1;
    this.max = this.dataset.maxQuantity ? Number(this.dataset.maxQuantity) : null;
    this.quantity = Number(this.dataset.cartQuantity) || 0;
    this.addButton = this.querySelector('[data-product-card-add]');
    this.quantityControl = this.querySelector('[data-product-card-quantity]');
    this.quantityInput = this.querySelector('[data-product-card-quantity-input]');
    this.minusButton = this.querySelector('[data-product-card-minus]');
    this.plusButton = this.querySelector('[data-product-card-plus]');
    this.error = this.querySelector('[data-product-card-cart-error]');
    this.abortController = new AbortController();

    const { signal } = this.abortController;

    this.addButton?.addEventListener('click', this.addToCart, { signal });
    this.minusButton?.addEventListener('click', this.decreaseQuantity, { signal });
    this.plusButton?.addEventListener('click', this.increaseQuantity, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal });

    this.render();
  }

  disconnectedCallback() {
    this.closest('product-card')?.classList.remove('product-card--in-cart');
    this.abortController?.abort();
  }

  handleCartUpdate = async (event) => {
    let cart = event.detail?.resource;
    if (!this.variantId) return;

    if (!cart?.items) {
      cart = await this.fetchCart();
    }

    if (!cart?.items) return;

    this.quantity = this.getVariantQuantity(cart);
    this.render();
  };

  addToCart = () => {
    if (!this.available) return;

    this.changeCart({
      url: Theme.routes.cart_add_url,
      body: { items: [{ id: Number(this.variantId), quantity: this.min }] },
    });
  };

  increaseQuantity = () => {
    if (!this.available) return;

    this.setCartQuantity(this.quantity + this.step);
  };

  decreaseQuantity = () => {
    // Respect the minimum order quantity (e.g. Gunkan rolls): stepping below the
    // minimum removes the line instead of leaving an invalid in-between amount.
    const next = this.quantity - this.step;
    this.setCartQuantity(next < this.min ? 0 : next);
  };

  setCartQuantity(quantity) {
    this.changeCart({
      url: Theme.routes.cart_change_url,
      body: { id: String(this.variantId), quantity },
    });
  }

  async changeCart({ url, body }) {
    if (!this.variantId || this.classList.contains('is-loading')) return;

    this.setLoading(true);
    this.setError('');

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
      const payload = await response.json();

      if (!response.ok || payload.status) {
        throw new Error(payload.description || payload.message || 'Cart update failed');
      }

      const cart = payload.items ? payload : await this.fetchCart();
      this.quantity = this.getVariantQuantity(cart);
      this.dispatchEvent(
        new CartUpdateEvent(cart, this.id || this.variantId, {
          itemCount: cart.item_count,
          source: 'product-card-cart-control',
          variantId: this.variantId,
        })
      );
    } catch (error) {
      this.setError(error.message || 'Cart update failed');
    } finally {
      this.setLoading(false);
      this.render();
    }
  }

  async fetchCart() {
    ProductCardCartControl.cartFetchPromise ??= fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .finally(() => {
        ProductCardCartControl.cartFetchPromise = null;
      });

    return ProductCardCartControl.cartFetchPromise;
  }

  getVariantQuantity(cart) {
    const item = cart?.items?.find((cartItem) => String(cartItem.variant_id) === String(this.variantId));
    return item?.quantity || 0;
  }

  setLoading(isLoading) {
    this.classList.toggle('is-loading', isLoading);
    this.addButton?.toggleAttribute('disabled', isLoading || !this.available);
    this.minusButton?.toggleAttribute('disabled', isLoading);
    this.plusButton?.toggleAttribute('disabled', isLoading);
  }

  setError(message) {
    if (!this.error) return;

    this.error.textContent = message;
    this.error.hidden = !message;
  }

  render() {
    const isInCart = this.quantity > 0;

    this.classList.toggle('is-in-cart', isInCart);
    this.closest('product-card')?.classList.toggle('product-card--in-cart', isInCart);
    this.addButton?.toggleAttribute('hidden', isInCart);
    this.quantityControl?.toggleAttribute('hidden', !isInCart);
    this.addButton?.toggleAttribute('disabled', !this.available);

    if (this.quantityInput) {
      this.quantityInput.value = String(this.quantity);
      this.quantityInput.setAttribute('value', String(this.quantity));
    }

    const maxReached = this.max !== null && this.quantity >= this.max;
    this.plusButton?.toggleAttribute('disabled', !this.available || maxReached);
    this.minusButton?.toggleAttribute('disabled', this.quantity <= 0);
  }
}

if (!customElements.get('product-card-cart-control')) {
  customElements.define('product-card-cart-control', ProductCardCartControl);
}
